import type { PickedImageFile } from '../types/electron-bridge';

const DEFAULT_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

export interface PickImageOptions {
  title?: string;
  extensions?: string[];
}

function acceptAttribute(extensions: string[]): string {
  return Array.from(new Set([...extensions.map((extension) => `.${extension}`), 'image/*'])).join(',');
}

/** The browser's own file input: no way to choose the folder it opens in. */
function pickWithFileInput(options: PickImageOptions): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptAttribute(options.extensions ?? DEFAULT_EXTENSIONS);
    input.style.position = 'fixed';
    input.style.left = '-10000px';
    document.body.appendChild(input);

    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };

    input.addEventListener('change', () => finish(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => finish(null), { once: true });
    input.click();
  });
}

/**
 * Pick one image, ready to hand to the image editor.
 *
 * In the desktop app this is the OS dialog (the `electron:pick-image` handler in
 * `electron/imagePicker.ts`), which opens in the folder the picker itself
 * remembers — a plain `<input type="file">` cannot set that, and Chromium's
 * shared "last used folder" is whatever unrelated upload ran before. Everywhere
 * else (browser, Capacitor) it falls back to the file input.
 *
 * A failing native dialog (file too large, dialog error) throws: the caller is
 * the one that can tell the user, and quietly opening a second dialog would hide
 * the reason.
 */
export async function pickImageFile(options: PickImageOptions = {}): Promise<File | null> {
  const bridge = window.electron?.pickImageFile;
  if (!bridge) return pickWithFileInput(options);

  const picked: PickedImageFile | null = await bridge(options);
  if (!picked || picked.canceled || !picked.data) return null;

  // Copy into a fresh buffer: `File` takes an ArrayBuffer-backed view, and the
  // IPC payload's type only promises ArrayBufferLike.
  const bytes = Uint8Array.from(picked.data);
  return new File([bytes], picked.name || 'image', { type: picked.mimeType || 'image/png' });
}

/** The first image file in a drop, so a stray folder or text drop is ignored. */
export function firstImageFromDrop(event: DragEvent): File | null {
  const items = event.dataTransfer?.files;
  if (!items || items.length === 0) return null;
  for (const file of Array.from(items)) {
    if (file.type.startsWith('image/')) return file;
  }
  return null;
}
